import React, { useEffect } from 'react';
import { Container, Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, { PaginationProvider } from 'react-bootstrap-table2-paginator';
import {
  FaCaretSquareDown, FaCaretSquareUp, FaCaretUp, FaCaretDown,
} from 'react-icons/fa';
import InviteClients from '../InviteClients';
import EmptyTableMessage from '../../../../components/EmptyTableMessage';
import Pagination from '../../../../components/Pagination/index';
import ErrorMessage from '../../../../components/ErrorMessage';
import { ActionsFormatter, clientNameFormatter, HealthscoreFormatter } from './Formatters/index';
import { SET_PAGINATION_PAGE_CLIENTS } from '../../duck/types';
import style from './ClientsTable.module.scss';
import { Tooltip, Icon } from 'chaser-components';
import LoadingMessage from '../../../../components/LoadingMessage';
import usePush from '../../../../hooks/usePush';
import ClientService from '../../../services/client-service';

const sortIcon = (order) => {
  if (order === 'asc') return <FaCaretSquareDown className={style.orderIcon} />;
  if (order === 'desc') return <FaCaretSquareUp className={style.orderIcon} />;

  return (
    <div className={style.iconSort}>
      <FaCaretUp className={style.unsortIcon__up} />
      <FaCaretDown className={style.unsortIcon__down} />
    </div>
  );
};

const healthScoreHeader = (order) => (
  <>
    <Tooltip
      className={style.healthscoreTooltip}
      position="bottom"
      content={`
          A client’s health score is a representation 
          of the effectiveness of their credit control function in the last 6 months. 
          It analyses their payment trends and currently outstanding debtors.`
      }
    >
      <Icon
        size={16}
        className={style.infoIcon}
        name="warning-circle"
        color="Mid Neutral 2"
      />
    </Tooltip>
    {sortIcon(order)}
  </>
);

const ClientsTable = () => {
  const { page } = useSelector(state => state.clientsReducer);
  const { bundle } = useSelector(state => state.loginReducer);
  const dispatch = useDispatch();

  const { execute, response: noHealthscoreClients, loading, error } = usePush(ClientService.getClients, {
    errorToast: true,
  });

  const { execute: eHealthscores, response: healthscoreClients } = usePush(ClientService.getHealthscores, {
    errorToast: true,
  });

  let clients = healthscoreClients || noHealthscoreClients;

  useEffect(() => {
    async function fetchClients() {
      const [err, res] = await execute(bundle);
      if (err) return;
      dispatch({ type: 'SET_TOTAL_SIZE', totalSize: res.length })
    }
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle]);

  useEffect(() => {
    async function fetchHealthscores() {
      if (noHealthscoreClients) await eHealthscores(noHealthscoreClients);
    }
    fetchHealthscores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noHealthscoreClients])

  if (loading) return <LoadingMessage data-testid="loading-message" />;
  if (error) return <ErrorMessage />;

  const columns = [{
    dataField: 'name',
    text: 'CLIENT',
    sort: true,
    sortCaret: sortIcon,
    headerStyle: {
      cursor: 'pointer',
    },
    style: {
      width: '45%',
      textAlign: 'center',
    },
    formatter: clientNameFormatter,
  }, {
    dataField: 'healthscoreData.score',
    text: 'HEALTH SCORE',
    sort: true,
    sortFunc: (a, b, order) => {
      if (a === b) {
        return 0;
      } else if (isNaN(a)) {
        return 1;
      } else if (isNaN(b)) {
        return -1;
      }
      return order === 'asc' ? b - a : a - b;
    },
    sortCaret: healthScoreHeader,
    headerStyle: {
      cursor: 'pointer',
      textAlign: 'center',
    },
    style: {
      width: '20%',
    },
    formatter: (score, row) => (
      <HealthscoreFormatter
        healthscore={score}
        healthscoreData={row?.healthscoreData}
        loading={row?.healthscoreLoading}
        name={row?.name}
        shortCode={row?.ShortCode}
      />
    ),
  }, {
    dataField: 'accountingSoftware',
    text: 'SOFTWARE',
    sort: true,
    sortCaret: sortIcon,
    headerStyle: {
      cursor: 'pointer',
    },
    style: {
      width: '15%',
    },
  }, {
    dataField: 'status',
    text: 'STATUS',
    headerAlign: 'left',
    headerStyle: {
      cursor: 'pointer',
    },
    style: {
      textAlign: 'left',
    },
    sort: true,
    sortCaret: sortIcon,
  }, {
    dataField: 'ShortCode',
    text: 'ACTIONS',
    style: {
      textAlign: 'right',
      width: '5%',
    },
    headerAlign: 'right',
    formatter: (cell, row) => (
      <ActionsFormatter
        orgName={row?.name}
        shortCode={cell}
        isDisabled={row?.isDisabled}
        isConnected={row?.isConnected}
        refetch={() => execute(bundle)}
      />
    )
  }];

  return (
    <div className={style.clientsTableContainer}>
      <Container className={style.clientsTable}>
        <PaginationProvider
          pagination={paginationFactory({
            custom: true,
            totalSize: clients ? clients.length : 0,
            page,
            sizePerPage: 10,
          })}
        >
          {
            ({
              paginationProps,
              paginationTableProps,
            }) => (
              <div data-testid="client-table">
                <BootstrapTable
                  keyField="id"
                  classes={style.table}
                  data={clients || []}
                  columns={columns}
                  bordered={false}
                  noDataIndication={() => (
                    <EmptyTableMessage
                      loading={loading}
                      message="There are no clients added yet"
                    >
                      <InviteClients />
                    </EmptyTableMessage>
                  )}
                  headerClasses={style.header}
                  rowClasses={style.row}
                  defaultSorted={[{
                    dataField: 'name',
                    order: 'asc',
                  }]}
                  {...paginationTableProps}
                />
                <Row align="center">
                  <Col sm="12" md={{ size: 6, offset: 3 }}>
                    <Pagination
                      paginationProps={paginationProps}
                      dispatchType={SET_PAGINATION_PAGE_CLIENTS}
                    />
                  </Col>
                </Row>
              </div>
            )
          }
        </PaginationProvider>
      </Container>
    </div>
  );
}

export default ClientsTable;

ClientsTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  fetchClientDetails: PropTypes.func.isRequired,
  sizePerPage: PropTypes.number.isRequired,
  totalSize: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  bundle: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  page: PropTypes.number.isRequired,
  clients: PropTypes.arrayOf(PropTypes.object).isRequired,
  error: PropTypes.string,
};

ClientsTable.defaultProps = {
  error: undefined,
};
